import React, { useEffect, useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PAGED_AUTHORIZATIONS_QUERY } from '../../queries';
import { ServiceContext } from 'providers/ServiceProvider';
import { Card } from '@totalsoft/rocket-ui';
import AuthorizationListContainer from './AuthorizationListContainer';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { useQuery } from 'hooks/apollo';
import AuthorizationActions from './actions/AuthorizationActions';
import { useApolloClient } from '@apollo/client';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ListBrowser, { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';

export const authorizationStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'Authorization.Name',
      type: 'text'
    },
    {
      name: 'userGroups',
      caption: 'Authorization.UserGroup',
      type: 'text',
      isClient: true
    }
  ],
  orderByFields: [
    {
      caption: 'Authorization.Name',
      value: 'Name'
    }
  ],
  pager: {
    orderBy: 'Name',
    direction: 'asc'
  }
});

const AuthorizationListBrowser = ({ onRefineAuthorization, onSaveFnChanged }) => {
  const [activeService] = useContext(ServiceContext);
  const client = useApolloClient();
  const { addActions } = useListBrowserUtils(authorizationStore);
  const [t] = useTranslation();

  const stateRef = useRef({});

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        name: 'pagedAuthorizations',
        query: PAGED_AUTHORIZATIONS_QUERY
      }
    }),
    []
  );

  useQuery(USER_GROUPS_QUERY, {
    onCompleted: data => {
      stateRef.current = {
        ...stateRef.current,
        data: data.userGroupsConfig
      };
      const { handleUserGroupsChange } = stateRef.current;
      handleUserGroupsChange && handleUserGroupsChange(data.userGroupsConfig);
    }
  });

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedAuthorizations' });
      client.cache.gc();

      stateRef.current = {
        ...stateRef.current,
        changedPermissions: undefined,
        disabledSave: undefined,
        handleUserGroupsChange: undefined
      };
    }
  }, [activeService, client]);

  useEffect(() => {
    addActions([
      {
        name: 'Add',
        icon: <AuthorizationActions />
      }
    ]);
  }, [addActions, t]);

  const listContainer = useCallback(
    () => (
      <AuthorizationListContainer onRefineAuthorization={onRefineAuthorization} onSaveFnChanged={onSaveFnChanged} stateRef={stateRef} />
    ),
    [onRefineAuthorization, onSaveFnChanged]
  );

  return (
    <Card icon={ListAltIcon}>
      <ListBrowser name='authorization-browser' store={authorizationStore} ItemsComponent={listContainer} />
      <ApolloFetchingComponent store={authorizationStore} options={fetchingOptions} />
    </Card>
  );
};

AuthorizationListBrowser.propTypes = {
  onRefineAuthorization: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired
};

export default AuthorizationListBrowser;
